import { wartungsintervall } from '../../../../utils/apiTypes';
import { wartungsintervallState } from './wartungsintervallTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, putWithAuth, deleteWithAuth } from "../../../../utils/api";
import type { RootState, AppDispatch } from "../../../../app/store";
import { logError } from "../../../../utils/utils";
import { openSnackbar } from "../../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../../utils/errorHandling';

export const wartungsintervallDummy: wartungsintervall
  = {
  name: "",
  kommentar: "",
  zugeordnet: false
};

const initialState: wartungsintervallState
  = {
  wartungsintervallListe: [],
  altesWartungsintervall: wartungsintervallDummy,
  aktuellesWartungsintervall: wartungsintervallDummy,
  newEntry: false,
  editEntry: false,
  editDirty: 0,
  viewMode: false,
  error: [],
  listLoading: false,
  sorting: [
    {
      field: 'name',
      sort: 'asc',
    },
  ]
};

const wartungsintervallSlice = createSlice({
  name: "wartungsintervall",
  initialState,
  reducers:
  {
    updateWartungsintervallListe: (state, action: PayloadAction<wartungsintervall>) => {
      const updateIndex = state.wartungsintervallListe.findIndex(
        (x: wartungsintervall) => x.name == action.payload.name
      );
      const firstPart = state.wartungsintervallListe.slice(0, updateIndex);
      let lastPart = Array<wartungsintervall>();
      if (updateIndex + 1 < state.wartungsintervallListe.length)
        lastPart = state.wartungsintervallListe.slice(updateIndex + 1);
      state.wartungsintervallListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.name.localeCompare(b.name));
    },
    removeFromWartungsintervallListe: (state, action: PayloadAction<wartungsintervall>) => {
      const updateIndex = state.wartungsintervallListe.findIndex(
        (x) => x.name == action.payload.name
      );
      const firstPart = state.wartungsintervallListe.slice(0, updateIndex);
      let lastPart = Array<wartungsintervall>();
      if (updateIndex + 1 < state.wartungsintervallListe.length)
        lastPart = state.wartungsintervallListe.slice(updateIndex + 1);
      state.wartungsintervallListe = [...firstPart, ...lastPart];
    },
    addAktuellesWartungsintervallToWartungsintervallListe: (state) => {
      const wandlerArray = state.wartungsintervallListe;
      wandlerArray.push(state.aktuellesWartungsintervall);
      state.wartungsintervallListe = [...wandlerArray].sort((a, b) => a.name.localeCompare(b.name));
    },
    setWartungsintervallListe: (state, action: PayloadAction<Array<wartungsintervall>>) => {
      state.wartungsintervallListe = action.payload;
    },
    setAktuellesWartungsintervall: (state, action: PayloadAction<wartungsintervall>) => {
      state.aktuellesWartungsintervall = action.payload;
      state.altesWartungsintervall = action.payload;
    },
    setAltesWartungsintervall: (state, action: PayloadAction<wartungsintervall>) => {
      state.altesWartungsintervall = action.payload;
    },
    resetAktuellesWartungsintervall: (state) => {
      state.aktuellesWartungsintervall = state.altesWartungsintervall;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setName: (state, action: PayloadAction<string>) => {
      state.aktuellesWartungsintervall.name = action.payload;
      state.editDirty =
        state.aktuellesWartungsintervall.name ==
          state.altesWartungsintervall.name
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuellesWartungsintervall.kommentar = action.payload;
      state.editDirty =
        state.aktuellesWartungsintervall.kommentar ==
          state.altesWartungsintervall.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  }
});


//Selectors
//======================================================================
export const selectWartungsintervallListe = (state: RootState): Array<wartungsintervall> =>
  state.wartungsintervall.wartungsintervallListe;
export const selectAktuellesWartungsintervall = (state: RootState): wartungsintervall =>
  state.wartungsintervall.aktuellesWartungsintervall;
export const selectEditEntry = (state: RootState): boolean =>
  state.wartungsintervall.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.wartungsintervall.newEntry;
export const selectEditDirty = (state: RootState): number =>
  state.wartungsintervall.editDirty;
export const selectViewMode = (state: RootState): boolean => state.wartungsintervall.viewMode;
export const selectError = (state: RootState): boolean => state.wartungsintervall.error.length > 0;
export const selectListLoading = (state: RootState): boolean => state.wartungsintervall.listLoading;
export const selectSorting = (state: RootState): GridSortModel => state.wartungsintervall.sorting;

//Actions
//=====================================================================

export const getWartungsintervallListe = (url: string, typ: string) => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth(url, {})
    .then((response) => {
      dispatch(setWartungsintervallListe(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateWartungsintervall = (aktuellesWartungsintervall: wartungsintervall, url: string, typ: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth(url , {}, aktuellesWartungsintervall, aktuellesWartungsintervall.name)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${typ} mit Name ${aktuellesWartungsintervall.name} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAltesWartungsintervall(aktuellesWartungsintervall));
      dispatch(updateWartungsintervallListe(aktuellesWartungsintervall));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const updateAktuellesWartungsintervall = (aktuellesWartungsintervall: wartungsintervall): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setAktuellesWartungsintervall(aktuellesWartungsintervall));
  dispatch(setViewMode());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesWartungsintervall());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesWartungsintervall());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellesWartungsintervall(wartungsintervallDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createWartungsintervall = (aktuellesWartungsintervall: wartungsintervall, url: string, typ: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth(url , {}, aktuellesWartungsintervall, aktuellesWartungsintervall.name)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${typ} mit Name ${aktuellesWartungsintervall.name} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(addAktuellesWartungsintervallToWartungsintervallListe());
      dispatch(setAktuellesWartungsintervall(wartungsintervallDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const deleteWartungsintervall = (aktuellesWartungsintervall: wartungsintervall, url: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth(url , { name: aktuellesWartungsintervall.name }, aktuellesWartungsintervall.name)
    .then(() => {
      dispatch(setAktuellesWartungsintervall(wartungsintervallDummy));
      dispatch(removeFromWartungsintervallListe(aktuellesWartungsintervall));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(openSnackbar({
        message: `${aktuellesWartungsintervall.name} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const {
  removeFromWartungsintervallListe,
  setWartungsintervallListe,
  setAktuellesWartungsintervall,
  resetAktuellesWartungsintervall,
  setAltesWartungsintervall,
  updateWartungsintervallListe,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setName,
  setKommentar,
  addAktuellesWartungsintervallToWartungsintervallListe,
  setListLoading,
  setSorting
} = wartungsintervallSlice.actions;
export default wartungsintervallSlice.reducer;